import { watchExperimentFlags } from '../utils/experimentFlagUpdates';
import { useEffect, useRef, useState } from 'react';
import { capturePostHogEvent, POSTHOG_KEY } from '../utils/analytics';
import {
  HOSTED_CHECKOUT_EXPERIMENT_NAME,
  HOSTED_CHECKOUT_FEATURE_FLAG,
} from '../utils/hostedCheckout';
import { isPreviewExperimentOverrideHost } from '../utils/experimentOverrideHosts';
import {
  CHECKOUT_EXPERIMENT_STORAGE_KEY,
  CHECKOUT_EXPERIMENT_QA_STORAGE_KEY,
  CHECKOUT_EXPERIMENT_IDENTITY_RESET,
  isProductionCheckoutHost,
  readCheckoutExperimentStorage,
  writeCheckoutExperimentStorage,
} from '../utils/checkoutExperimentIdentity';

const VALID_VARIANTS = new Set(['control', 'stripe_hosted']);
const DEFAULT_DEPENDENCIES = {
  captureEvent: capturePostHogEvent,
  posthogKey: POSTHOG_KEY,
  isProductionCheckoutRuntime: () => isProductionCheckoutHost(window.location.hostname),
  isQaCheckoutRuntime: () =>
    isPreviewExperimentOverrideHost() &&
    new URLSearchParams(window.location.search).get('checkoutExperience') === 'experiment',
  getPreviewVariant: () => {
    if (!isPreviewExperimentOverrideHost()) return null;
    const value = new URLSearchParams(window.location.search).get('checkoutExperience');
    return VALID_VARIANTS.has(value) ? value : null;
  },
};

const newId = (prefix = 'assign_') => {
  if (window.crypto?.randomUUID) return `${prefix}${window.crypto.randomUUID()}`;
  // These are deduplication identifiers, never authentication secrets.
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const value = Math.floor(Math.random() * 16);
    return (character === 'x' ? value : (value & 3) | 8).toString(16);
  });
  return `${prefix}${uuid}`;
};

const isStringValue = (value) => value?.constructor === String;

const readAssignment = (storageKey) => {
  try {
    const assignment = JSON.parse(readCheckoutExperimentStorage(storageKey) || 'null');
    return assignment &&
      VALID_VARIANTS.has(assignment.variant) &&
      isStringValue(assignment.assignmentId) &&
      isStringValue(assignment.subjectId) &&
      isStringValue(assignment.exposedAt)
      ? assignment
      : null;
  } catch {
    return null;
  }
};

const writeAssignment = (storageKey, assignment) => {
  writeCheckoutExperimentStorage(storageKey, JSON.stringify(assignment));
};

const clearAssignment = (storageKey) => {
  writeCheckoutExperimentStorage(storageKey, null);
};

const resolvePostHogFlag = () =>
  new Promise((resolve) => {
    let settled = false;
    let pollTimer;
    let timeoutId;
    let unsubscribe;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(pollTimer);
      window.clearTimeout(timeoutId);
      if (unsubscribe) unsubscribe();
      resolve(value);
    };
    const bind = () => {
      const client = window.posthog;
      if (!client?.getFeatureFlag) {
        pollTimer = window.setTimeout(bind, 50);
        return;
      }
      let immediateValue;
      try {
        immediateValue = client.getFeatureFlag(HOSTED_CHECKOUT_FEATURE_FLAG, { send_event: false });
      } catch {
        finish(null);
        return;
      }
      if (VALID_VARIANTS.has(immediateValue) || immediateValue === false) {
        finish(immediateValue);
        return;
      }
      if (!client.onFeatureFlags) {
        finish(immediateValue);
        return;
      }
      try {
        unsubscribe = client.onFeatureFlags(() => {
          try {
            finish(client.getFeatureFlag(HOSTED_CHECKOUT_FEATURE_FLAG, { send_event: false }));
          } catch {
            finish(null);
          }
        });
      } catch {
        finish(null);
      }
      if (settled && unsubscribe) unsubscribe();
    };
    timeoutId = window.setTimeout(() => finish(null), 3000);
    bind();
  });

export default function useCheckoutExperienceExperiment(
  surface,
  dependencies = DEFAULT_DEPENDENCIES,
  { excludeNewVisitors = false } = {},
) {
  const {
    captureEvent,
    posthogKey,
    isProductionCheckoutRuntime = DEFAULT_DEPENDENCIES.isProductionCheckoutRuntime,
    isQaCheckoutRuntime = DEFAULT_DEPENDENCIES.isQaCheckoutRuntime,
    getPreviewVariant = DEFAULT_DEPENDENCIES.getPreviewVariant,
  } = dependencies;
  const [result, setResult] = useState({
    variant: 'control',
    isLoading: true,
    enrolled: false,
    assignmentId: null,
  });
  const [identityRevision, setIdentityRevision] = useState(0);
  const generation = useRef(0);
  const isProduction = isProductionCheckoutRuntime();
  const isQa = !isProduction && isQaCheckoutRuntime();
  const previewVariant = getPreviewVariant();

  useEffect(() => {
    const reset = () => {
      generation.current += 1;
      setIdentityRevision((revision) => revision + 1);
    };
    const storageChanged = (event) => {
      if (
        event.newValue === null &&
        [CHECKOUT_EXPERIMENT_STORAGE_KEY, CHECKOUT_EXPERIMENT_QA_STORAGE_KEY, null].includes(
          event.key,
        )
      )
        reset();
    };
    window.addEventListener(CHECKOUT_EXPERIMENT_IDENTITY_RESET, reset);
    window.addEventListener('storage', storageChanged);
    return () => {
      window.removeEventListener(CHECKOUT_EXPERIMENT_IDENTITY_RESET, reset);
      window.removeEventListener('storage', storageChanged);
    };
  }, []);

  useEffect(() => {
    let active = true;
    const currentGeneration = generation.current;
    const storageKey = isQa ? CHECKOUT_EXPERIMENT_QA_STORAGE_KEY : CHECKOUT_EXPERIMENT_STORAGE_KEY;
    let cached = readAssignment(storageKey);
    let unsubscribe;
    // Rob, 2026-09-30: a buyer who arrives with a promo code cannot use Stripe's
    // page (Stripe will not remove or replace a code it already applied), so
    // they are kept out of the test entirely: no flag evaluation, no stored
    // assignment, no exposure event. PostHog then counts only buyers who really
    // saw their arm. A buyer already in the test keeps their arm.
    if (excludeNewVisitors && !cached) {
      setResult({
        variant: 'control',
        isLoading: false,
        enrolled: false,
        excluded: true,
        assignmentId: null,
      });
      return () => {
        active = false;
      };
    }
    if (previewVariant) {
      clearAssignment(CHECKOUT_EXPERIMENT_STORAGE_KEY);
      setResult({
        variant: previewVariant,
        isLoading: false,
        enrolled: true,
        assignmentId: newId('assign_preview_'),
        subjectId: newId('checkout_qa_'),
        exposedAt: new Date().toISOString(),
      });
      return () => {
        active = false;
      };
    }
    if (!posthogKey || (!isProduction && !isQa)) {
      clearAssignment(storageKey);
      setResult({ variant: 'control', isLoading: false, enrolled: false, assignmentId: null });
      return () => {
        active = false;
      };
    }

    setResult({ variant: 'control', isLoading: true, enrolled: false, assignmentId: null });
    const apply = (rawVariant) => {
      if (!active || currentGeneration !== generation.current) return;
      if (!VALID_VARIANTS.has(rawVariant)) {
        clearAssignment(storageKey);
        cached = null;
        setResult({ variant: 'control', isLoading: false, enrolled: false, assignmentId: null });
        return;
      }
      // A 100% control rollout must revoke a saved hosted arm as well as an
      // inactive flag. Keep metadata only when PostHog confirms that same arm.
      if (cached && cached.variant !== rawVariant) cached = null;
      const subjectId = isQa ? newId('checkout_qa_') : window.posthog?.get_distinct_id?.();
      if (!cached && !subjectId) {
        setResult({ variant: 'control', isLoading: false, enrolled: false, assignmentId: null });
        return;
      }
      const assignment = cached || {
        variant: rawVariant,
        assignmentId: newId(isQa ? 'assign_preview_' : 'assign_'),
        subjectId,
        exposedAt: new Date().toISOString(),
        exposureRecorded: false,
        flagEventId: newId(''),
        exposureEventId: newId(''),
      };
      cached = assignment;
      writeAssignment(storageKey, assignment);
      const eventProps = {
        distinct_id: assignment.subjectId,
        experiment_name: HOSTED_CHECKOUT_EXPERIMENT_NAME,
        variant: assignment.variant,
        feature_flag: HOSTED_CHECKOUT_FEATURE_FLAG,
        feature_flag_value: assignment.variant,
        checkout_experience_assignment_id: assignment.assignmentId,
        checkout_experience_subject_id: assignment.subjectId,
        checkout_experience_surface: surface,
        checkout_experience_exposed_at: assignment.exposedAt,
        [`$feature/${HOSTED_CHECKOUT_FEATURE_FLAG}`]: assignment.variant,
        is_experiment_active: true,
      };
      if (isQa) {
        eventProps.is_test = true;
        eventProps.traffic_environment = 'development';
      }
      // Older stored assignments already sent exposure. Retry only explicitly
      // unrecorded new assignments with stable IDs to make SDK retries idempotent.
      if (assignment.exposureRecorded === false) {
        const flagAccepted = captureEvent(
          '$feature_flag_called',
          {
            ...eventProps,
            $feature_flag: HOSTED_CHECKOUT_FEATURE_FLAG,
            $feature_flag_response: assignment.variant,
            $insert_id: `${assignment.assignmentId}:feature_flag_called`,
            $timestamp: assignment.exposedAt,
          },
          { uuid: assignment.flagEventId, timestamp: new Date(assignment.exposedAt) },
        );
        const exposureAccepted = captureEvent(
          'experiment_exposure',
          {
            ...eventProps,
            $insert_id: `${assignment.assignmentId}:experiment_exposure`,
            $timestamp: assignment.exposedAt,
          },
          { uuid: assignment.exposureEventId, timestamp: new Date(assignment.exposedAt) },
        );
        if (flagAccepted !== false && exposureAccepted !== false) {
          cached = { ...assignment, exposureRecorded: true };
          writeAssignment(storageKey, cached);
        }
      }
      const { variant, assignmentId, exposedAt } = assignment;
      setResult({
        variant,
        assignmentId,
        subjectId: assignment.subjectId,
        exposedAt,
        isLoading: false,
        enrolled: true,
      });
    };
    void resolvePostHogFlag().then((rawVariant) => {
      if (!active || currentGeneration !== generation.current) return;
      apply(rawVariant);
      unsubscribe = watchExperimentFlags(window.posthog, () => {
        try {
          apply(
            window.posthog?.getFeatureFlag(HOSTED_CHECKOUT_FEATURE_FLAG, { send_event: false }),
          );
        } catch {
          apply(null);
        }
      });
    });
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [
    captureEvent,
    excludeNewVisitors,
    previewVariant,
    isProduction,
    isQa,
    identityRevision,
    posthogKey,
    surface,
  ]);

  return result;
}
